import {
  Entity,
  getSessionId,
  init,
  LogClient,
  Metric,
  MonsterverseLog,
  MonsterversePerfStats,
  ProductEvent,
  setSessionId,
} from "@classdojo/log-client";
import pick from "lodash/pick";

import { AppMetadata } from "../appBridge/AppMetadata";
import { UserManager } from "../controllers/UserManager";
import { getLauncherStartupOptions } from "../controllers/Utility";
import { SCHOOL_FEATURE_SWITCHES } from "../featureSwitches/constants";
import { IFeatureSwitchManager } from "../featureSwitches/IFeatureSwitchManager";
import { AutoFlushingQueue } from "../utils/AutoFlushingQueue";
import { Env } from "../utils/env";
import { convertToSeconds } from "../utils/NumberExtension";
import { createWindowTimerFunctions } from "../utils/Timer";
import { debugInfoSystem } from "./DebugInfo";

type LogMethod = "info" | "warn" | "error" | "exception";

export class Logger {
  private static logClient: LogClient;
  private static featureSwitchManager: IFeatureSwitchManager;
  static appMetadata: AppMetadata = {};
  private static serverID?: string;
  private static countryCode?: string;
  private static playerCoordinates?: { x: number; y: number; z: number };
  private static connectedUsers?: number;
  private static webGlVersion?: string;
  private static appReadyTime = -1;
  private static lastProductEventSent: ProductEvent | undefined;
  private static lastProductEventTime = -1;
  private static readonly undefinedStr = "--";
  private static _logsQueue: AutoFlushingQueue<MonsterverseLog>;
  private static _eventsQueue: AutoFlushingQueue<ProductEvent>;
  private static _perfStatsQueue: AutoFlushingQueue<MonsterversePerfStats>;

  // eslint-disable-next-line sonarjs/cognitive-complexity
  static initialize(
    featureSwitchManager: IFeatureSwitchManager,
    options: {
      env: Env;
      customEndpoint?: string;
      buildNumber?: string;
      appVersion?: string;
      server?: boolean;
      disabledForTest?: boolean;
    }
  ) {
    this.featureSwitchManager = featureSwitchManager;
    if (!Logger.logClient) {
      Logger.logClient = init({
        initNewInstance: true,
        env: options.env === Env.Prod ? "prod" : "dev",
        site: "monsterworld",
        customEndpoint: options.customEndpoint,
        buildNumber: options.buildNumber || `${window.build}`,
        appVersion: options.appVersion || `${window.build}`,
        devParseStubbedLogs: true,
        disabledForTest: options.disabledForTest,
      });
      Logger._eventsQueue = new AutoFlushingQueue<ProductEvent>(60000, 50, (logs) => {
        try {
          Logger.logClient.logMonsterverseProductEvents(logs);
        } catch (e) {
          Logger.logException(new Error("Failed to logMonsterverseEvents"));
        }
      });
      Logger._logsQueue = new AutoFlushingQueue<MonsterverseLog>(60000, 50, (logs) =>
        Logger.logClient.logMonsterverseMessage(logs)
      );
      Logger._perfStatsQueue = new AutoFlushingQueue<MonsterversePerfStats>(60000, 50, (stats) =>
        Logger.logClient.logMonsterversePerfStats(stats)
      );
      if (typeof document !== "undefined" && document?.addEventListener) {
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "hidden") {
            Logger.flushLogs();
          }
        });
      }

      //Disable most console logging when in production (only keeping error for debugging purposes - and its very limited amount of prints)
      if (options.env === Env.Prod && !options.server) {
        const ignoreLogs = () => {
          //NOOP
        };
        console.log = ignoreLogs;
        console.info = ignoreLogs;
        console.warn = ignoreLogs;
        console.debug = ignoreLogs;
        window.time = ignoreLogs;
        window.timeEnd = ignoreLogs;
      } else {
        createWindowTimerFunctions();
      }
    }

    Logger.serverID = undefined;
    Logger.countryCode = undefined;
    Logger.playerCoordinates = undefined;
    Logger.connectedUsers = undefined;
    Logger.webGlVersion = undefined;
  }

  static setAppReadyTime(time: number) {
    Logger.appReadyTime = time;
  }

  static setFeatureSwitches(featureSwitches: Record<string, string>) {
    Logger.logClient.featureSwitches = featureSwitches;
  }

  static setProductArea(productArea: string) {
    Logger.logClient.productArea = productArea;
  }

  static setServerID(serverID: string) {
    Logger.serverID = serverID;
  }

  static setCountryCode(countryCode: string) {
    Logger.countryCode = countryCode;
  }

  static setPlayerCoordinates(coords: { x: number; y: number; z: number }) {
    Logger.playerCoordinates = coords;
  }

  static setConnectedUsers(users: number) {
    Logger.connectedUsers = users;
  }

  static setWebGlVersion(webGlVersion: string) {
    Logger.webGlVersion = webGlVersion;
  }

  static logEvent(rawData: string | ProductEvent, { appendEntityType = true } = {}) {
    if (typeof rawData === "string") {
      rawData = { eventName: rawData };
    }

    Logger.lastProductEventSent = rawData;
    Logger.lastProductEventTime = Date.now();

    if (Logger.serverID) {
      rawData.metadata = { ...rawData.metadata, serverID: Logger.serverID };
    }
    if (Logger.countryCode) {
      rawData.metadata = { ...rawData.metadata, countryCode: Logger.countryCode };
    }
    if (Logger.playerCoordinates) {
      rawData.metadata = { ...rawData.metadata, coordinates: Logger.playerCoordinates };
    }
    if (Logger.connectedUsers != undefined) {
      rawData.metadata = { ...rawData.metadata, connectedUsers: Logger.connectedUsers };
    }
    if (Logger.webGlVersion) {
      rawData.metadata = { ...rawData.metadata, webGlVersion: Logger.webGlVersion };
    }

    const entityTypePrefix = Logger.getEntityTypePrefix(rawData.eventName, appendEntityType);

    // some old iOS client apps are not sending platform on the app metadata
    const platform = this.appMetadata?.platform || "ios";
    const eventName = `${platform.toLowerCase()}.${entityTypePrefix}${rawData.eventName}`;
    rawData.eventName = eventName;

    this._eventsQueue.push({
      clientCreatedAt: new Date(),
      ...pick(this.appMetadata, "osVersion", "device", "deviceID", "deviceModel"),
      ...rawData,
    });
  }

  private static getEntityTypePrefix(eventName: string, appendEntityType: boolean) {
    let entityTypePrefix = "";
    if (appendEntityType) {
      if (eventName.startsWith("teacher.") || eventName.startsWith("student.")) {
        throw new Error(
          `Don't use 'student.' or 'teacher.' prefixes for ${eventName}. They will be added based on the entityType`
        );
      }

      if (!Logger.logClient.entityType) {
        // try to get entity type from startup query string params
        const { entityType } = getLauncherStartupOptions();
        entityTypePrefix = `${entityType}.`;
      } else {
        entityTypePrefix =
          Logger.logClient.entityType === "studentUser" ? "student." : `${Logger.logClient.entityType}.`;
      }
    }

    return entityTypePrefix;
  }

  static logStartExperiment(experiment: string) {
    // ignore experiments for other platforms
    if (!this.featureSwitchManager.isValidFeatureSwitchForCurrentPlatform(experiment)) {
      return;
    }
    if (
      !this.featureSwitchManager.featureSwitches[experiment] &&
      Object.values(SCHOOL_FEATURE_SWITCHES).includes(experiment)
    ) {
      // students without school ids dont fetch schoolid-based feature switches. Don't error on those.
      return;
    }

    Logger.logClient.logStartExperiment(experiment);
  }

  static logConvertExperiment(experiment: string) {
    // ignore experiments for other platforms
    if (!this.featureSwitchManager.isValidFeatureSwitchForCurrentPlatform(experiment)) {
      return;
    }
    if (
      !this.featureSwitchManager.featureSwitches[experiment] &&
      Object.values(SCHOOL_FEATURE_SWITCHES).includes(experiment)
    ) {
      // students without school ids dont fetch schoolid-based feature switches. Don't error on those.
      return;
    }

    Logger.logClient.logConvertExperiment(experiment);
  }

  static logException(error: Error, additionalContext: Record<string, unknown> = {}) {
    if (window.iosCancelButtonPressed) {
      return;
    }

    Logger.logClient.logException(error, this.getExtendedLogContext(additionalContext));
  }

  static logInfo(message: string, context: Record<string, unknown> = {}) {
    Logger.logMessage("info", message, context);
  }

  static logWarn(message: string, context: Record<string, unknown> = {}) {
    Logger.logMessage("warn", message, context);
  }

  static logError(message: string, context: Record<string, unknown> = {}) {
    Logger.logMessage("error", message, context);
  }

  private static _monsterverseLogsQueue: AutoFlushingQueue<MonsterverseLog>;
  private static logMessage(
    verbosity: "error" | "warn" | "info",
    message: string,
    context: Record<string, unknown> = {}
  ) {
    Logger._logsQueue.push({
      message,
      verbosity,
      context: this.getExtendedLogContext(context),
    });
  }

  private static getExtendedLogContext(context: Record<string, unknown> | undefined = undefined) {
    return {
      userAgent: window.navigator.userAgent,
      deviceModel: this.appMetadata.deviceModel,
      userType: UserManager.instance.user?.type,
      serverID: this.serverID,
      schoolID: UserManager.instance.getSchoolId(),
      countryCode: this.countryCode,
      webGlVersion: this.webGlVersion,
      appState: {
        secondsSinceAppReady:
          this.appReadyTime > 0 ? convertToSeconds(Date.now() - this.appReadyTime) : this.undefinedStr,
        lastProductEvent: {
          event: this.lastProductEventSent,
          secondsSinceEvent: this.lastProductEventSent
            ? convertToSeconds(Date.now() - this.lastProductEventTime)
            : this.undefinedStr,
        },
        featureSwitches: this.featureSwitchManager.featureSwitches,
      },
      ...debugInfoSystem.getDebugInfo(),
      ...context,
    };

    // Some other values we may want to keep track of:

    // - total # of players
    // - local player coords
    // - memory usage (if available)
  }

  static printAppState() {
    console.log(this.getExtendedLogContext());
  }

  static logPerfStats(stats: MonsterversePerfStats) {
    stats.deviceModel = stats.deviceModel ?? this.appMetadata.deviceModel;
    Logger._perfStatsQueue.push(stats);
  }

  /**
   * @summary Only throws an exception when working in developer mode, but still logs the exception to our logging services.
   * @remarks Optionally, use the logMethod parameter to change the verbosity of the reporting to the backend.
   * @param error Error object or string to be thrown.
   * @param logMethodOnProd Logging method to backend used to control what verbosity we wish to report.
   */
  static throwIfTesting(error: Error, logMethodOnProd?: LogMethod): void;
  static throwIfTesting(error: string, logMethodOnProd?: LogMethod): void;
  static throwIfTesting(error: Error | string, logMethodOnProd: LogMethod = "exception"): void {
    let throwable: Error | undefined;
    if (error instanceof Error) {
      throwable = error;
    } else if (typeof error === "string" || (error as unknown) instanceof String) {
      throwable = new Error(error);
    }

    if (throwable === undefined) {
      return;
    }

    if (Logger.logClient.env !== "prod") {
      throw throwable;
    }

    switch (logMethodOnProd) {
      default:
      case "info":
        Logger.logInfo(throwable.toString(), this.getExtendedLogContext({ ...throwable }));
        break;
      case "warn":
        Logger.logWarn(throwable.toString(), this.getExtendedLogContext({ ...throwable }));
        break;
      case "error":
        Logger.logError(throwable.toString(), this.getExtendedLogContext({ ...throwable }));
        break;
      case "exception":
        Logger.logException(
          throwable,
          this.getExtendedLogContext({
            ...throwable,
          })
        );
        break;
    }
  }

  static logMetrics(metrics: Metric[]) {
    if (metrics.length) {
      Logger.logClient?.logMetrics(metrics);
    }
  }

  static flushLogs() {
    Logger._logsQueue.flush();
    Logger._eventsQueue.flush();
    Logger._perfStatsQueue.flush();
  }

  static setEntityId(id?: string) {
    if (Logger.logClient) Logger.logClient.entityId = id;
  }

  static setEntityType(entityType?: Entity) {
    if (Logger.logClient) Logger.logClient.entityType = entityType;
  }

  static getSessionId() {
    return getSessionId();
  }

  static setSessionId(sessionId: string) {
    setSessionId(sessionId);
  }
}
